import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Star, ChevronDown, ChevronUp, X, Filter as FilterIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from './CurrencySelector';
import { Slider } from './ui/slider';
import { Checkbox } from './ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Faceted filters sidebar for /shop.
 * Loads /api/search/facets and renders price range slider + rating + vendor checkboxes.
 *
 * Calls onChange({ priceRange, ratings, vendorIds }) whenever a filter changes.
 */
export const SearchFacets = ({ categoryId, search, value, onChange }) => {
  const { language } = useLanguage();
  const T = (fr, en) => (language === 'fr' ? fr : en);
  const { format } = useCurrency();
  const [facets, setFacets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sectionsOpen, setSectionsOpen] = useState({ price: true, rating: true, vendor: true });

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const params = {};
        if (categoryId) params.category_id = categoryId;
        if (search) params.search = search;
        const r = await axios.get(`${API}/search/facets`, { params });
        setFacets(r.data);
      } catch (_) { /* silent */ } finally { setLoading(false); }
    };
    fetch();
  }, [categoryId, search]);

  const filters = value || { priceRange: null, ratings: [], vendorIds: [] };

  const setPriceRange = (range) => onChange({ ...filters, priceRange: range });
  const toggleRating = (rating) => {
    const next = filters.ratings.includes(rating)
      ? filters.ratings.filter((r) => r !== rating)
      : [...filters.ratings, rating];
    onChange({ ...filters, ratings: next });
  };
  const toggleVendor = (vid) => {
    const next = filters.vendorIds.includes(vid)
      ? filters.vendorIds.filter((v) => v !== vid)
      : [...filters.vendorIds, vid];
    onChange({ ...filters, vendorIds: next });
  };
  const clearAll = () => onChange({ priceRange: null, ratings: [], vendorIds: [] });
  const hasFilters = filters.priceRange || filters.ratings.length || filters.vendorIds.length;

  const Section = ({ title, sectionKey, children }) => {
    const open = sectionsOpen[sectionKey];
    return (
      <div className="border-b border-[#0F4C5C]/10 pb-4 mb-4 last:border-0 last:mb-0 last:pb-0">
        <button
          type="button"
          onClick={() => setSectionsOpen({ ...sectionsOpen, [sectionKey]: !open })}
          className="w-full flex items-center justify-between mb-3 text-[#0F4C5C] font-semibold text-sm uppercase tracking-wider"
        >
          <span>{title}</span>
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {open && children}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-[#0F4C5C]/10 p-5 animate-pulse h-72" data-testid="facets-loading" />
    );
  }

  if (!facets || facets.total === 0) {
    return null;
  }

  return (
    <aside className="bg-white rounded-2xl border border-[#0F4C5C]/10 p-5 shadow-sm" data-testid="search-facets">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FilterIcon className="w-4 h-4 text-[#0F4C5C]" />
          <h3 className="font-bold text-[#0F4C5C]">{T('Filtrer', 'Filter')}</h3>
          <span className="text-xs text-[#0F4C5C]/60">({facets.total})</span>
        </div>
        {hasFilters && (
          <button onClick={clearAll} className="text-xs text-[#0F4C5C]/70 hover:text-red-600 flex items-center gap-1" data-testid="clear-facets">
            <X className="w-3 h-3" /> {T('Tout effacer', 'Clear all')}
          </button>
        )}
      </div>

      {/* Price range */}
      {facets.price.max > facets.price.min && (
        <Section title={T('Prix', 'Price')} sectionKey="price">
          <div className="px-1">
            <Slider
              min={facets.price.min}
              max={facets.price.max}
              step={1}
              value={filters.priceRange || [facets.price.min, facets.price.max]}
              onValueChange={(range) => setPriceRange(range)}
              className="my-3"
              data-testid="price-slider"
            />
            <div className="flex justify-between text-sm text-[#0F4C5C]/80 font-medium mt-2">
              <span>{format(filters.priceRange?.[0] ?? facets.price.min)}</span>
              <span>{format(filters.priceRange?.[1] ?? facets.price.max)}</span>
            </div>
          </div>
        </Section>
      )}

      {/* Ratings */}
      {facets.ratings.length > 0 && (
        <Section title={T('Note', 'Rating')} sectionKey="rating">
          <div className="space-y-2">
            {facets.ratings.map((r) => (
              <label key={r.rating} className="flex items-center gap-2.5 cursor-pointer hover:bg-[#FBF8F3] -mx-2 px-2 py-1.5 rounded-lg" data-testid={`facet-rating-${r.rating}`}>
                <Checkbox
                  checked={filters.ratings.includes(r.rating)}
                  onCheckedChange={() => toggleRating(r.rating)}
                />
                <div className="flex items-center gap-1 flex-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className={`w-3.5 h-3.5 ${i < r.rating ? 'fill-[#E9C46A] text-[#E9C46A]' : 'text-[#0F4C5C]/20'}`}
                    />
                  ))}
                  <span className="text-xs text-[#0F4C5C]/60 ml-1">& +</span>
                </div>
                <span className="text-xs text-[#0F4C5C]/60">{r.count}</span>
              </label>
            ))}
          </div>
        </Section>
      )}

      {/* Vendors */}
      {facets.vendors.length > 1 && (
        <Section title={T('Vendeur', 'Vendor')} sectionKey="vendor">
          <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
            {facets.vendors.map((v) => (
              <label key={v.vendor_id} className="flex items-center gap-2.5 cursor-pointer hover:bg-[#FBF8F3] -mx-2 px-2 py-1.5 rounded-lg">
                <Checkbox
                  checked={filters.vendorIds.includes(v.vendor_id)}
                  onCheckedChange={() => toggleVendor(v.vendor_id)}
                />
                <span className="flex-1 text-sm text-[#0F4C5C] truncate">{v.vendor_name}</span>
                <span className="text-xs text-[#0F4C5C]/60">{v.count}</span>
              </label>
            ))}
          </div>
        </Section>
      )}
    </aside>
  );
};
